import { useEffect, useState } from "react";

/**
 * Shared client-side weather state.
 *
 * Design: a module-level singleton + subscriber list so every component
 * that consumes this hook renders from the SAME fetched payload. This
 * matters because the Morning Brief masthead needs two independent
 * surfaces (WeatherWidget chip + SunriseSunsetRail) that would each
 * otherwise re-prompt for geolocation and hammer Open-Meteo.
 *
 * The first consumer to mount kicks off geolocation + fetch. Subsequent
 * consumers subscribe and re-render whenever the shared state changes.
 * Persistence still uses `localStorage` so the state survives full page
 * reloads for the cache TTL.
 */

const CACHE_KEY = "yadidos_weather_v1";
const CACHE_TTL_MS = 30 * 60 * 1000;

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed;
  } catch { return null; }
};
const writeCache = (data) => {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, ts: Date.now() })); } catch {}
};

// ─── Singleton state + pub-sub ─────────────────────────────────────────
let sharedState = (() => {
  const cached = readCache();
  return cached ? { status: "ok", ...cached } : { status: "idle" };
})();
let inflight = false;
const subscribers = new Set();
const setSharedState = (next) => {
  sharedState = typeof next === "function" ? next(sharedState) : next;
  subscribers.forEach((fn) => fn(sharedState));
};

// ─── Reverse-geocode + weather fetch ──────────────────────────────────
async function reverseGeocode(lat, lon) {
  // Photon (Komoot/OSM) — free, CORS-enabled, no API key. Prefers the
  // most human-scale label so neighbourhoods aren't collapsed into the
  // parent city.
  try {
    const url = `https://photon.komoot.io/reverse?lat=${lat}&lon=${lon}&limit=1`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const j = await res.json();
    const p = j?.features?.[0]?.properties || {};
    return p.district || p.city || p.name || p.state || p.country || null;
  } catch { return null; }
}

async function fetchWeather(lat, lon) {
  const url =
    `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
    `&current=temperature_2m,weather_code` +
    `&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max,sunrise,sunset` +
    `&temperature_unit=fahrenheit&timezone=auto&forecast_days=1`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("weather fetch failed");
  const j = await res.json();
  const tempF = j?.current?.temperature_2m;
  const code = j?.current?.weather_code;
  const hiF = j?.daily?.temperature_2m_max?.[0];
  const loF = j?.daily?.temperature_2m_min?.[0];
  const rain = j?.daily?.precipitation_probability_max?.[0];
  const sunriseIso = j?.daily?.sunrise?.[0];
  const sunsetIso = j?.daily?.sunset?.[0];
  const tzPlace = j?.timezone?.split("/").pop()?.replace(/_/g, " ") || "";
  const round = (n) => (typeof n === "number" ? Math.round(n) : null);
  const toC = (f) => (typeof f === "number" ? Math.round((f - 32) * 5 / 9) : null);
  const geoPlace = await reverseGeocode(lat, lon);
  return {
    tempF: round(tempF),
    tempC: toC(tempF),
    hiF: round(hiF),
    loF: round(loF),
    hiC: toC(hiF),
    loC: toC(loF),
    rain: typeof rain === "number" ? Math.max(0, Math.min(100, Math.round(rain))) : null,
    code: typeof code === "number" ? code : null,
    place: geoPlace || tzPlace,
    // Open-Meteo returns tz-local ISO strings without offset when
    // timezone=auto is set, so `new Date(sunriseIso)` parses them
    // against the browser's local clock (which is what we want).
    sunriseIso: sunriseIso || null,
    sunsetIso: sunsetIso || null,
  };
}

function requestGeoAndLoad() {
  if (inflight) return;
  inflight = true;
  if (!navigator.geolocation) {
    inflight = false;
    setSharedState({ status: "unsupported" });
    return;
  }
  setSharedState((s) => ({ ...s, status: s.status === "ok" ? "ok" : "loading" }));
  navigator.geolocation.getCurrentPosition(
    async (pos) => {
      try {
        const data = await fetchWeather(pos.coords.latitude, pos.coords.longitude);
        setSharedState({ status: "ok", ...data });
        writeCache(data);
      } catch {
        setSharedState({ status: "error" });
      } finally {
        inflight = false;
      }
    },
    () => {
      inflight = false;
      setSharedState({ status: "denied" });
    },
    { enableHighAccuracy: false, timeout: 8000, maximumAge: 15 * 60 * 1000 }
  );
}

// ─── Public hook ──────────────────────────────────────────────────────
/**
 * Subscribe to the shared weather state. Options:
 *   - `autoRequest` (default true): the first mounting consumer kicks
 *     off geolocation. Set to false for surfaces (like the rail) that
 *     should only render if data is already resolved.
 */
export default function useWeather({ autoRequest = true } = {}) {
  const [state, setState] = useState(sharedState);
  useEffect(() => {
    subscribers.add(setState);
    setState(sharedState);
    if (autoRequest && sharedState.status === "idle") requestGeoAndLoad();
    return () => { subscribers.delete(setState); };
  }, [autoRequest]);
  return { ...state, refetch: requestGeoAndLoad };
}
